import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  type RawAxiosRequestHeaders,
} from 'axios';
import { t } from 'i18next';

import {
  currentDeviceId,
  DOMAIN,
  REASON,
  reportFailure,
  reportInfo,
  reportWarning,
  sanitizeUrl,
  trace,
} from '~/services/telemetry';
import { mainStore, userStore } from '~/stores';

import { company } from '../company/Company';
import {
  API_URL,
  ENDPOINT,
  MAX_REQUEST_ATTEMPT,
  REQUEST_TIMEOUT,
  SSR_MAX_REQUEST_ATTEMPT,
  SSR_REQUEST_TIMEOUT,
} from './constants';
import { makePostOnce, reportableBody } from './postOnce';
import {
  type PendingRefresh,
  type RefreshRotation,
  refreshJournal,
  type SessionSignIn,
  type SignInMethod,
} from './refreshJournal';
import { describeTokenClaims, type TokenClaims } from './tokenClaims';
import { tokenStorage } from './tokenStorage';

export interface ApiErrorResponse {
  code?: string;
  error?: string | boolean;
  errors?:
    | string[]
    | {
        message: string;
        payload: { message: string };
      }[];
  message?:
    | string
    | {
        children: unknown[];
        constraints: {
          isLength: string;
          isNotEmpty: string;
          isString: string;
          matches: string;
        };
        property: string;
        target: Record<string, unknown>;
      }[];
  data?: {
    errorCode?: string;
    refusalReason?: string;
    resultCode?: string;
    payload?: { err_code?: string; error_code?: string };
    [key: string]: unknown;
  };
  statusCode?: number;
  status?: number;
}

type OmitDistributive<T, K extends PropertyKey> = T extends any
  ? T extends Record<any, any>
    ? OmitItem<OmitRecursively<T, K>>
    : T
  : never;

type OmitItem<T> = Record<any, unknown> & { [P in keyof T]: T[P] };

export type OmitRecursively<T, K extends PropertyKey> = Omit<
  { [P in keyof T]: OmitDistributive<T[P], K> },
  K
>;

interface RequestAPIMethods {
  get(
    url: string,
    data?: Record<string, any>,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse['data']>;

  post(
    url: string,
    data?: Record<string, any>,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse['data']>;

  postOnce(
    url: string,
    data?: Record<string, unknown>,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse['data']>;

  put(
    url: string,
    data?: Record<string, any>,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse['data']>;

  patch(
    url: string,
    data?: Record<string, string | number | boolean | undefined>,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse['data']>;

  delete(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse['data']>;

  refresh(
    url: string,
    data?: Record<string, string | number | boolean | undefined>,
  ): Promise<AxiosResponse['data']>;
}

const isSSR = typeof window === 'undefined';

const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: isSSR ? SSR_REQUEST_TIMEOUT : REQUEST_TIMEOUT,
});

const maxAttempts = isSSR ? SSR_MAX_REQUEST_ATTEMPT : MAX_REQUEST_ATTEMPT;

export const addToken = (config?: AxiosRequestConfig): AxiosRequestConfig => {
  const target = config || {};
  const headers: RawAxiosRequestHeaders = { ...(target.headers || {}) };

  delete headers.Authorization;
  delete headers.authorization;
  delete headers['x-company-id'];

  if (userStore.isAuthorized && userStore.token) {
    headers.Authorization = `Bearer ${userStore.token}`;
  } else {
    headers['x-company-id'] = company.config.id;
  }

  if (company.is('mcosmetic')) {
    headers['x-client-platform'] = mainStore.appPlatform || 'web';
  }

  return Object.assign(target, { headers });
};

const MAX_ORDER_NEW_409_RETRIES = 10;

const isTerminalOrderNew409 = (
  response: AxiosResponse | undefined,
): boolean => {
  const data = response?.data as ApiErrorResponse | undefined;
  if (!data) return false;

  const kind = (data.data as { kind?: unknown } | undefined)?.kind;
  if (kind === 'validation') return true;

  if (Array.isArray(data.errors)) {
    return data.errors.some((entry) => {
      const message = typeof entry === 'string' ? entry : entry?.message;
      return typeof message === 'string' && message.includes('VALIDATION');
    });
  }

  return false;
};

export const requestWrapper = async (
  request: () => Promise<AxiosResponse>,
): Promise<AxiosResponse> => {
  let count = maxAttempts;
  let orderNew409Retries = 0;
  while (count > 0) {
    count--;
    try {
      return await request();
    } catch (error) {
      if (error instanceof AxiosError) {
        if (
          !error.response &&
          (error.code === 'ECONNABORTED' || error.message === 'Network Error')
        ) {
          if (count) {
            const attempt = count;
            await new Promise((resolve) =>
              setTimeout(resolve, (maxAttempts - attempt) * 1000),
            );
            continue;
          } else {
            mainStore.pushAlert('error', t('errors:oops'));
            return Promise.reject(error);
          }
        }

        if (
          error.response?.status === 409 &&
          error.response?.config?.url === ENDPOINT.orders.new
        ) {
          /**
           * In case when user creates new order, and system returns error code 409,
           * front should do new attempt unless server will not return error code not equal 409 or success.
           *
           * Exceptions that must NOT be retried:
           * - Validation errors (e.g. MINDBOX_VALIDATION_ERROR): server will keep
           *   returning the same 409 forever, especially because the request is
           *   sent with a stable Idempotency-Key.
           * - Hard cap on consecutive 409 retries to guarantee loop termination.
           */
          if (
            isTerminalOrderNew409(error.response) ||
            orderNew409Retries >= MAX_ORDER_NEW_409_RETRIES
          ) {
            return Promise.reject(error);
          }

          orderNew409Retries++;
          count = 4;
          await new Promise((resolve) => setTimeout(resolve, 1000));
          continue;
        }
      }

      return Promise.reject(error);
    }
  }
  return Promise.reject();
};

export const RequestAPI: RequestAPIMethods = {
  get: (url, data, config) =>
    requestWrapper(() =>
      axiosInstance.get(url + mainStore.convertObjToGet(data || {}), config),
    ),

  post: (url, data, config) =>
    requestWrapper(() => axiosInstance.post(url, data, config)),

  put: (url, data, config) =>
    requestWrapper(() => axiosInstance.put(url, data, config)),

  patch: (url, data, config) =>
    requestWrapper(() => axiosInstance.patch(url, data, config)),

  delete: (url, config) =>
    requestWrapper(() => axiosInstance.delete(url, config)),

  /**
   * Deliberately not wrapped into requestWrapper: the backend rotates the
   * refresh token, so a retry after a timeout would replay an already spent
   * token and kill the session.
   */
  refresh: (url, data) =>
    axiosInstance.post(url, data, {
      headers: { Authorization: `Bearer ${userStore.refreshToken}` },
    }),

  /**
   * POST that is never re-sent and never wrapped into `requestWrapper`, which
   * repeats a request whose answer was lost — for a payment, a second charge.
   * `makePostOnce` is unit-tested to call the POST exactly once on a timeout.
   * Interceptors still apply: the token, the single replay after a 401 the
   * server refused, and logging without the body.
   */
  postOnce: makePostOnce((url, data, config) =>
    axiosInstance.post(url, data, config),
  ),
};

const loggingErrorResponse = ({ data, status, config }: AxiosResponse) => {
  if (status === 401 || status === 404) {
    return;
  }
  if (status === 400 && config.url?.includes('eta/get')) {
    return;
  }
  let url = config.url || '';
  if (!url.includes('http')) {
    // Join base and path with a single slash so the logged URL matches the URL
    // axios actually requests (combineURLs); a naive concat drops the slash and
    // produces misleading addresses like "api2.example.comorders/...".
    const base = (config.baseURL || '').replace(/\/+$/, '');
    const path = (config.url || '').replace(/^\/+/, '');
    url = `${base}/${path}`;
  }
  const method = config.method?.toUpperCase() ?? 'GET';
  // The query string is dropped and ids in the path are masked before the URL
  // reaches the title. Left whole it put a customer's phone number into the
  // name of a production issue — `[422] GET .../sms/send?phone_number=…` — and
  // gave every distinct id its own issue, so the same broken endpoint could
  // never be totalled up.
  const path = sanitizeUrl(url);

  // Only server faults become events. Everything else was `info` before this
  // module existed, and `info` is what became a breadcrumb everywhere else —
  // applying the rule here too keeps a bad connection from spending the
  // session's reporting budget on a dozen 4xx before anything real breaks.
  if (status < 500) {
    // Deliberately without the request body. A breadcrumb rides on every event
    // for the rest of the session, and by the time an interceptor sees it the
    // body has already been serialised to a string — an OTP or a phone number
    // in there is past the reach of key-based redaction and would then travel
    // with every unrelated error that followed.
    trace(
      DOMAIN.api,
      `${status} ${method} ${path}`,
      { url: path, method: config.method, status },
      'warning',
    );
    return;
  }

  reportWarning(DOMAIN.api, `${status} ${method} ${path}`, {
    reason: REASON.api.requestFailed,
    fingerprint: [String(status), method, path],
    extras: {
      request: {
        url: path,
        method: config.method,
        request: reportableBody(config),
      },
      response: data,
      status,
    },
  });
};

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  isTokenRetry?: boolean;
};

const isTokenRefreshUrl = (url?: string): boolean =>
  Boolean(url?.includes(ENDPOINT.customer.token.refresh));

const isLogoutUrl = (url?: string): boolean =>
  Boolean(url?.includes(ENDPOINT.customer.logout));

const isSessionIndependentUrl = (url?: string): boolean =>
  isTokenRefreshUrl(url) ||
  isLogoutUrl(url) ||
  Boolean(url?.includes(ENDPOINT.customer.code.request)) ||
  Boolean(url?.includes(ENDPOINT.customer.code.verify));

/**
 * A refresh token is rotated by the backend on every successful refresh, so a
 * failure with 401/403 means the stored one is already spent — the session is
 * unrecoverable. Any other failure (network, timeout, 5xx) says nothing about
 * the session and must not log the customer out.
 */
const isSessionExpiredError = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) {
    return false;
  }

  const status = error.response?.status;

  return status === 401 || status === 403;
};

const getRequestToken = (config: AxiosRequestConfig): string => {
  const header = config.headers?.Authorization ?? config.headers?.authorization;

  return typeof header === 'string' ? header.replace('Bearer ', '') : '';
};

/**
 * Records the moment a customer is signed out without asking to be.
 *
 * Nothing else in the app records it: 401 never becomes an `[api]` event and
 * the logout itself is silent, so until now the only evidence a session had
 * ended was the customer saying so in a support thread days later. That is why
 * "how often does this happen, and to whom" has had no answer.
 *
 * Deliberately a log line and not instrumentation — it runs once, at the point
 * of failure, and costs nothing on the path where refreshes succeed.
 */
const secondsSince = (iso: string): number =>
  Math.round((Date.now() - new Date(iso).getTime()) / 1000);

const describeLostAttempt = (
  lost: PendingRefresh | null,
  sentJti: string | null,
) =>
  lost && {
    jti: lost.jti,
    startedAt: lost.startedAt,
    ageSeconds: secondsSince(lost.startedAt),
    sameJti: lost.jti === sentJti,
  };

const describeLastRotation = (
  rotation: RefreshRotation | null,
  sentJti: string | null,
) =>
  rotation && {
    ...rotation,
    ageSeconds: secondsSince(rotation.at),
    sentWasRotatedAway: rotation.fromJti === sentJti,
    sentIsLatestIssued: rotation.toJti === sentJti,
  };

const describeSignIn = (signIn: SessionSignIn | null) =>
  signIn && {
    ...signIn,
    ageSeconds: secondsSince(signIn.at),
    sameDevice: signIn.deviceId === currentDeviceId(),
  };

const describeSavedPair = () => {
  const stored = tokenStorage.read();
  const signedIn = stored?.status === 'signedIn' ? stored : null;

  return {
    storeSavedAt: userStore.tokensSavedAt,
    storageStatus: stored?.status ?? null,
    storageSavedAt: signedIn?.savedAt ?? null,
    storageMatchesStore: signedIn
      ? signedIn.refresh === userStore.refreshToken
      : null,
  };
};

const describeSession = () => {
  const { signIn, counters } = refreshJournal.session();

  return {
    signIn: describeSignIn(signIn),
    rotationsSinceSignIn: signIn ? counters.rotations : null,
    sessionCounters: counters,
    savedPair: describeSavedPair(),
  };
};

const describeSessionTiming = () => ({
  pageAgeSeconds:
    typeof performance === 'undefined'
      ? null
      : Math.round(performance.now() / 1000),
  inBackground: mainStore.inBackground,
  visibility: typeof document === 'undefined' ? null : document.visibilityState,
});

const reportAnswerLost = (
  lost: PendingRefresh,
  sentClaims: TokenClaims | null,
  rotationBefore: RefreshRotation | null,
): void => {
  const sentJti = sentClaims?.jti ?? null;

  reportWarning(DOMAIN.auth, 'Refresh answer lost, session survived', {
    reason: REASON.auth.refreshAnswerLost,
    once: true,
    extras: {
      lostAttempt: describeLostAttempt(lost, sentJti),
      lastRotation: describeLastRotation(rotationBefore, sentJti),
      ...describeSession(),
      refreshClaims: sentClaims,
      ...describeSessionTiming(),
    },
  });
};

const reportSessionEnded = (
  error: AxiosError,
  sentClaims: TokenClaims | null,
  lost: PendingRefresh | null,
  rotationBefore: RefreshRotation | null,
): void => {
  const status = error.response?.status ?? 0;
  const body = error.response?.data as { message?: unknown } | undefined;
  const expiresInSeconds = sentClaims?.expiresInSeconds;
  const sentJti = sentClaims?.jti ?? null;

  reportFailure(DOMAIN.auth, 'Session ended by a refused refresh token', {
    reason: REASON.auth.refreshRejected,
    fingerprint: [String(status)],
    once: true,
    extras: {
      lostAttempt: describeLostAttempt(lost, sentJti),
      lastRotation: describeLastRotation(rotationBefore, sentJti),
      ...describeSession(),
      ...describeSessionTiming(),
      status,
      // The backend's own wording, which separates a token it never had from
      // one it considers expired — the two point at different teams.
      backendMessage: typeof body?.message === 'string' ? body.message : null,
      // `false` here is the whole point: a token refused while still valid is
      // not a session that ran out. `null` when the token carried no `exp` to
      // read, which must not be reported as "did not expire" — this is the
      // field the catalogue tells people to read first.
      hadExpired:
        typeof expiresInSeconds === 'number' ? expiresInSeconds <= 0 : null,
      // Named without the word "token" on purpose. `capExtras` redacts any key
      // whose words include one of its sensitive list, matched on the key and
      // not the value — so `refreshTokenClaims` would arrive as `[redacted]`
      // and this log would carry nothing anyone could act on. The value holds
      // no credential; only public claims the token already carries.
      refreshClaims: sentClaims,
      isWebView:
        typeof window !== 'undefined' && Boolean(window.ReactNativeWebView),
    },
  });
};

export const refreshTokens = {
  promise: null as Promise<void> | null,

  get isRefreshing(): boolean {
    return this.promise !== null;
  },

  reset() {
    this.promise = null;
    refreshJournal.clear();
  },

  signedIn(method: SignInMethod) {
    const claims = describeTokenClaims(userStore.refreshToken);
    const previous = refreshJournal.signedIn({
      at: new Date().toISOString(),
      jti: claims?.jti ?? null,
      method,
      deviceId: currentDeviceId(),
    });

    reportInfo(DOMAIN.auth, 'Signed in', {
      reason: REASON.auth.signedIn,
      extras: {
        method,
        refreshClaims: claims,
        previousSignIn: describeSignIn(previous),
        isWebView:
          typeof window !== 'undefined' && Boolean(window.ReactNativeWebView),
      },
    });
  },

  signedOut() {
    reportInfo(DOMAIN.auth, 'Signed out', {
      reason: REASON.auth.signedOut,
      extras: {
        ...describeSession(),
        refreshClaims: describeTokenClaims(userStore.refreshToken),
      },
    });
  },

  sync(
    trigger: 'hydration' | 'focus' | 'refresh' | 'unauthorized',
  ): ReturnType<typeof userStore.syncTokens> {
    const winner = userStore.syncTokens();

    if (winner === 'storage') {
      refreshJournal.recovered();
    }

    if (winner) {
      trace(DOMAIN.auth, 'Tokens synced', { trigger, winner });
    }

    if (
      (winner === 'signedOut' || winner === 'signedIn') &&
      trigger !== 'hydration'
    ) {
      window.location.reload();
    }

    return winner;
  },

  wait(): Promise<void> {
    return this.promise
      ? this.promise.catch(() => undefined)
      : Promise.resolve();
  },

  run(): Promise<void> {
    if (!this.promise) {
      if (this.sync('refresh') === 'signedOut') {
        return Promise.reject(new Error('Signed out in another tab'));
      }

      // Read before the request leaves: the failure handler runs after the
      // store may already hold something else, and the claims of the token
      // that was actually refused are what the log is for.
      const sentClaims = describeTokenClaims(userStore.refreshToken);
      const sentJti = sentClaims?.jti ?? null;
      const rotationBefore = refreshJournal.lastRotation();
      const lost = refreshJournal.begin(sentJti);

      this.promise = userStore
        .requestNewToken()
        .then(() => {
          refreshJournal.rotated(
            sentJti,
            describeTokenClaims(userStore.refreshToken)?.jti ?? null,
          );

          if (lost) {
            reportAnswerLost(lost, sentClaims, rotationBefore);
          }
        })
        .catch((error: unknown) => {
          const isAnswerLost = error instanceof AxiosError && !error.response;

          if (isAnswerLost) {
            trace(
              DOMAIN.auth,
              'Refresh answer lost',
              { code: error.code, jti: sentJti },
              'warning',
            );
          } else {
            refreshJournal.settle();
          }

          if (isSessionExpiredError(error)) {
            reportSessionEnded(
              error as AxiosError,
              sentClaims,
              lost,
              rotationBefore,
            );

            mainStore.pushAlert('error', t('errors:sessionExpired'));
            userStore
              .logout({ cause: 'sessionExpired' })
              .catch(() => undefined);
          }

          throw error;
        })
        .finally(() => {
          this.promise = null;
        });
    }

    return this.promise;
  },

  async refresh(error: AxiosError) {
    const config = (error.config ?? error.response?.config) as
      | RetriableRequestConfig
      | undefined;

    if (!config?.url || error.response?.status !== 401) {
      return Promise.reject(error);
    }

    if (config.isTokenRetry || isSessionIndependentUrl(config.url)) {
      return Promise.reject(error);
    }

    if (!userStore.isAuthorized || !userStore.refreshToken) {
      return Promise.reject(error);
    }

    config.isTokenRetry = true;
    refreshJournal.unauthorizedRequest();

    if (this.sync('unauthorized') === 'signedOut') {
      return Promise.reject(error);
    }

    /**
     * The request may have been sent with a token that another request has
     * already rotated while it was in flight. Replaying it with the current
     * token is enough — refreshing again would spend a valid refresh token.
     */
    const isStaleRequestToken =
      Boolean(userStore.token) && getRequestToken(config) !== userStore.token;

    if (isStaleRequestToken) {
      await this.wait();
    } else {
      try {
        await this.run();
      } catch (_e) {
        return Promise.reject(error);
      }
    }

    if (!userStore.token) {
      return Promise.reject(error);
    }

    return axiosInstance.request(config);
  },
};

axiosInstance.interceptors.request.use(async (config) => {
  if (isTokenRefreshUrl(config.url)) {
    return config;
  }

  if (!isLogoutUrl(config.url)) {
    await refreshTokens.wait();
  }

  return addToken(config) as InternalAxiosRequestConfig;
});

axiosInstance.interceptors.response.use(
  ({ data }) => data,
  (error) => {
    if (error && error.response) {
      // Reporting must never mask the original AxiosError: 404/5xx
      // classification downstream depends on it propagating intact.
      try {
        loggingErrorResponse(error.response);
      } catch {
        // ignore
      }
    }
    return refreshTokens.refresh(error);
  },
);
